/**
 * A4 — Selector del emisor con el que se factura (cliente, expediente, abogado). Vacío =
 * «el que corresponda»: se resuelve al facturar según expediente, abogado, cliente o bufete.
 */
import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import type { IssuerDto } from '@toga/shared'

interface Props {
  value: string | null | undefined
  onChange: (id: string | null) => void
  /** Texto de la opción vacía. */
  emptyLabel: string
  disabled?: boolean
  className?: string
}

let cache: IssuerDto[] | null = null

export default function IssuerSelect({ value, onChange, emptyLabel, disabled, className }: Props) {
  const [issuers, setIssuers] = useState<IssuerDto[]>(cache ?? [])

  useEffect(() => {
    if (cache) return
    let alive = true
    api
      .get<{ items: IssuerDto[] }>('/issuers')
      .then((r) => {
        cache = r.items
        if (alive) setIssuers(r.items)
      })
      .catch(() => alive && setIssuers([]))
    return () => {
      alive = false
    }
  }, [])

  // Un emisor desactivado sigue mostrándose si es el elegido, para que no desaparezca en silencio.
  const options = issuers.filter((i) => i.active || i.id === value)

  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      className={
        className ??
        'w-full bg-transparent border border-base rounded-lg px-3 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'
      }
    >
      <option value="">{emptyLabel}</option>
      {options.map((i) => (
        <option key={i.id} value={i.id}>
          {i.name}
          {i.nif ? ` · ${i.nif}` : ''}
          {i.active ? '' : ' (desactivado)'}
        </option>
      ))}
    </select>
  )
}
